import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Banknote,
  Mail,
  Monitor,
  Moon,
  Pencil,
  Plus,
  Search,
  ShoppingCart,
  Sun,
  Trash,
  Users,
  Wrench,
} from "lucide-react";

import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  DataTable,
  PageHeader,
  StatCard,
  FormDialog,
  ConfirmDialog,
  StatusBadge,
  PaymentMethodBadge,
  FilterBar,
  TextField,
  SelectField,
  DataPagination,
  RowActions,
  EmptyState,
  ErrorState,
  ListSkeleton,
  TableSkeleton,
  CardSkeleton,
  TextSkeleton,
  Combobox,
  ColorSwatches,
  SpacingScale,
  RadiusScale,
  BorderWidths,
} from "@/components/design";

// ---------------------------------------------------------------------------
// Demo data for the DataTable section
// ---------------------------------------------------------------------------

interface DemoRow {
  id: string;
  name: string;
  status: string;
  amount: number;
}

const demoRows: DemoRow[] = [
  { id: "1", name: "Basic Wash", status: "completed", amount: 500 },
  { id: "2", name: "Full Detail", status: "in_progress", amount: 3500 },
  { id: "3", name: "Interior Clean", status: "pending", amount: 1200 },
  { id: "4", name: "Engine Wash", status: "hold", amount: 800 },
  { id: "5", name: "Premium Package", status: "cancelled", amount: 5000 },
];

const demoColumns: ColumnDef<DemoRow>[] = [
  { accessorKey: "name", header: "Service" },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "amount",
    header: "Amount",
    cell: ({ row }) => `KES ${row.original.amount.toLocaleString()}`,
  },
  {
    id: "actions",
    header: "Actions",
    cell: () => (
      <RowActions
        mode="inline"
        actions={[
          { label: "Edit", icon: <Pencil className="h-4 w-4" />, onClick: () => {} },
          {
            label: "Delete",
            icon: <Trash className="h-4 w-4" />,
            onClick: () => {},
            variant: "destructive",
          },
        ]}
      />
    ),
  },
];

type TableDemoState = "data" | "loading" | "error" | "empty";

// ---------------------------------------------------------------------------
// Demo form schema for the FormDialog section
// ---------------------------------------------------------------------------

const demoFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Enter a valid email"),
  role: z.string().min(1, "Role is required"),
});

type DemoFormValues = z.infer<typeof demoFormSchema>;

function ThemeToggleBar() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="sticky top-0 z-50 -mx-6 mb-4 flex items-center justify-between border-b bg-background/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:-mx-10 md:px-10">
      <span className="text-sm font-medium">Preview theme</span>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={theme === "light" ? "default" : "outline"}
          onClick={() => setTheme("light")}
        >
          <Sun className="mr-2 h-4 w-4" />
          Light
        </Button>
        <Button
          size="sm"
          variant={theme === "dark" ? "default" : "outline"}
          onClick={() => setTheme("dark")}
        >
          <Moon className="mr-2 h-4 w-4" />
          Dark
        </Button>
        <Button
          size="sm"
          variant={theme === "system" ? "default" : "outline"}
          onClick={() => setTheme("system")}
        >
          <Monitor className="mr-2 h-4 w-4" />
          System
        </Button>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

export default function DesignSystemPage() {
  const [tableState, setTableState] = React.useState<TableDemoState>("data");
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filterName, setFilterName] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [simplePage, setSimplePage] = React.useState(1);
  const [numberedPage, setNumberedPage] = React.useState(4);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectValue, setSelectValue] = React.useState("");
  const [comboboxValue, setComboboxValue] = React.useState("");

  const demoForm = useForm<DemoFormValues>({
    resolver: zodResolver(demoFormSchema),
    defaultValues: { name: "", email: "", role: "" },
  });

  const onDemoFormSubmit = (values: DemoFormValues) => {
    console.log("design system demo form submit", values);
    setFormDialogOpen(false);
    demoForm.reset();
  };

  const tableProps =
    tableState === "loading"
      ? { data: undefined, isLoading: true }
      : tableState === "error"
        ? { data: undefined, isError: true, error: { message: "Failed to load services" } }
        : tableState === "empty"
          ? { data: [] }
          : { data: demoRows };

  return (
    <div className="mx-auto max-w-5xl space-y-14 p-6 md:p-10">
      <ThemeToggleBar />
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Design System</h1>
        <p className="text-muted-foreground">
          Every component in <code>components/design</code>, built on the shadcn
          primitives in <code>components/ui</code>. Use this page to review before
          any feature page is switched over.
        </p>
      </div>

      <Section
        title="Colors"
        description="Semantic tokens from globals.css (CSS variables) plus the ad-hoc status colors in tailwind.config.js."
      >
        <ColorSwatches />
      </Section>

      <Section title="Spacing" description="Tailwind's default scale, n × 0.25rem.">
        <SpacingScale />
      </Section>

      <Section title="Borders & Radius">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <p className="mb-3 text-sm font-medium">Border radius</p>
            <RadiusScale />
          </div>
          <div>
            <p className="mb-3 text-sm font-medium">Border width</p>
            <BorderWidths />
          </div>
        </div>
      </Section>

      <Section title="Button" description="ui/button.tsx variants and sizes.">
        <div className="space-y-4 rounded-md border p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="default">Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
            <Button variant="default" disabled>
              Disabled
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm">Small</Button>
            <Button size="default">Default</Button>
            <Button size="lg">Large</Button>
            <Button size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Section>

      <Section title="Input" description="ui/input.tsx states.">
        <div className="grid grid-cols-1 gap-4 rounded-md border p-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="design-input-default">Default</Label>
            <Input id="design-input-default" placeholder="Enter a value" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="design-input-disabled">Disabled</Label>
            <Input id="design-input-disabled" placeholder="Disabled" disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="design-input-icon">With leading icon</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="design-input-icon" placeholder="Search..." className="pl-9" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="design-input-email">Email type</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="design-input-email" type="email" placeholder="you@example.com" className="pl-9" />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Select" description="ui/select.tsx, the raw primitive.">
        <div className="max-w-xs rounded-md border p-4">
          <Select value={selectValue} onValueChange={setSelectValue}>
            <SelectTrigger>
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="super-admin">Super Admin</SelectItem>
              <SelectItem value="company-admin">Company Admin</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section
        title="Combobox"
        description="Searchable single-select, built on Popover + Command (cmdk)."
      >
        <div className="max-w-xs rounded-md border p-4">
          <Combobox
            value={comboboxValue}
            onChange={setComboboxValue}
            placeholder="Select an employee"
            searchPlaceholder="Search employees..."
            options={[
              { value: "jane", label: "Jane Doe" },
              { value: "john", label: "John Smith" },
              { value: "mary", label: "Mary Wanjiru" },
              { value: "peter", label: "Peter Otieno" },
            ]}
          />
        </div>
      </Section>

      <Section title="PageHeader">
        <div className="rounded-md border p-6">
          <PageHeader
            title="Employees"
            description="Manage your team members and their roles"
            action={
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Employee
              </Button>
            }
          />
          <p className="text-sm text-muted-foreground">Page content goes below.</p>
        </div>
      </Section>

      <Section title="StatCard">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            icon={<Banknote className="h-5 w-5" />}
            label="Revenue"
            value="KES 128,400"
            title="Last 30 Days Turnover"
          />
          <StatCard
            icon={<ShoppingCart className="h-5 w-5" />}
            label="Orders"
            value={342}
            title="Total Orders"
          />
          <StatCard
            icon={<Wrench className="h-5 w-5" />}
            label="Today"
            value="KES 8,200"
            footer={
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-semibold">Cash</p>
                  <p className="text-muted-foreground">KES 5,000 (12)</p>
                </div>
                <div>
                  <p className="font-semibold">PDQ</p>
                  <p className="text-muted-foreground">KES 3,200 (7)</p>
                </div>
              </div>
            }
          />
        </div>
      </Section>

      <Section
        title="StatusBadge"
        description="One shared status → color mapping for order, payment, and transaction status."
      >
        <div className="flex flex-wrap gap-2">
          {["pending", "in_progress", "completed", "cancelled", "hold", "paid", "not_paid", "failed"].map(
            (status) => (
              <StatusBadge key={status} status={status} />
            )
          )}
          <PaymentMethodBadge method="cash" />
          <PaymentMethodBadge method="pdq" />
        </div>
      </Section>

      <Section title="EmptyState">
        <div className="rounded-md border">
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title="No employees yet"
            description="Add your first employee to get started."
            action={
              <Button size="sm" className="mt-2">
                <Plus className="mr-2 h-4 w-4" />
                Add Employee
              </Button>
            }
          />
        </div>
      </Section>

      <Section title="RowActions">
        <div className="flex items-center gap-8 rounded-md border p-4">
          <div>
            <p className="mb-2 text-sm font-medium">Inline mode</p>
            <RowActions
              mode="inline"
              actions={[
                { label: "Edit", icon: <Pencil className="h-4 w-4" />, onClick: () => {} },
                {
                  label: "Delete",
                  icon: <Trash className="h-4 w-4" />,
                  onClick: () => {},
                  variant: "destructive",
                },
              ]}
            />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Dropdown mode</p>
            <RowActions
              mode="dropdown"
              actions={[
                { label: "Edit", icon: <Pencil className="h-4 w-4" />, onClick: () => {} },
                {
                  label: "Delete",
                  icon: <Trash className="h-4 w-4" />,
                  onClick: () => {},
                  variant: "destructive",
                },
              ]}
            />
          </div>
        </div>
      </Section>

      <Section title="ErrorState">
        <div className="space-y-4">
          <ErrorState message="Failed to load employees" />
          <ErrorState
            message="Failed to load employees"
            onRetry={() => console.log("retry clicked")}
          />
        </div>
      </Section>

      <Section
        title="Loading skeletons"
        description="ListSkeleton (generic), TableSkeleton (row × column), CardSkeleton (matches StatCard), TextSkeleton (n lines)."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-md border p-4">
            <p className="mb-2 text-sm font-medium">ListSkeleton</p>
            <ListSkeleton rows={3} />
          </div>
          <div className="rounded-md border p-4">
            <p className="mb-2 text-sm font-medium">TableSkeleton</p>
            <TableSkeleton rows={3} columns={4} />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">CardSkeleton</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          </div>
          <div className="rounded-md border p-4">
            <p className="mb-2 text-sm font-medium">TextSkeleton</p>
            <TextSkeleton lines={3} />
          </div>
        </div>
      </Section>

      <Section
        title="DataTable"
        description="Unifies the loading / error / empty / populated table pattern."
      >
        <div className="mb-3 flex gap-2">
          {(["data", "loading", "error", "empty"] as TableDemoState[]).map((state) => (
            <Button
              key={state}
              size="sm"
              variant={tableState === state ? "default" : "outline"}
              onClick={() => setTableState(state)}
            >
              {state}
            </Button>
          ))}
        </div>
        <DataTable columns={demoColumns} {...tableProps} />
      </Section>

      <Section title="Pagination">
        <div className="space-y-6 rounded-md border p-4">
          <div>
            <p className="mb-2 text-sm font-medium">Simple (Previous / Next)</p>
            <DataPagination
              mode="simple"
              page={simplePage}
              pageCount={8}
              onPageChange={setSimplePage}
              totalItems={78}
              itemLabel="orders"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Numbered</p>
            <DataPagination
              mode="numbered"
              page={numberedPage}
              pageCount={12}
              onPageChange={setNumberedPage}
            />
          </div>
        </div>
      </Section>

      <Section title="FilterBar">
        <div className="rounded-md border p-4">
          <FilterBar
            open={filterOpen}
            onOpenChange={setFilterOpen}
            activeFilterCount={[filterName, filterStatus].filter(Boolean).length}
            onClear={() => {
              setFilterName("");
              setFilterStatus("");
            }}
            onApply={() => setFilterOpen(false)}
          >
            <div className="space-y-2">
              <Label htmlFor="design-filter-name">Name</Label>
              <Input
                id="design-filter-name"
                value={filterName}
                onChange={(event) => setFilterName(event.target.value)}
                placeholder="Search by name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="design-filter-status">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger id="design-filter-status">
                  <SelectValue placeholder="Any status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </FilterBar>
        </div>
      </Section>

      <Section
        title="FormDialog + FormField"
        description="react-hook-form + zod edit-modal pattern, using TextField/SelectField."
      >
        <div className="rounded-md border p-4">
          <Button onClick={() => setFormDialogOpen(true)}>Open Form Dialog</Button>
          <FormDialog
            open={formDialogOpen}
            onOpenChange={setFormDialogOpen}
            title="Edit Employee"
            form={demoForm}
            onSubmit={onDemoFormSubmit}
          >
            <TextField control={demoForm.control} name="name" label="Name" placeholder="Jane Doe" />
            <TextField
              control={demoForm.control}
              name="email"
              label="Email"
              type="email"
              placeholder="jane@example.com"
            />
            <SelectField
              control={demoForm.control}
              name="role"
              label="Role"
              options={[
                { value: "employee", label: "Employee" },
                { value: "company-admin", label: "Company Admin" },
              ]}
            />
          </FormDialog>
        </div>
      </Section>

      <Section title="ConfirmDialog">
        <div className="rounded-md border p-4">
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
            Delete Employee
          </Button>
          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Delete this employee?"
            description="This action cannot be undone."
            confirmLabel="Delete"
            onConfirm={() => setConfirmOpen(false)}
          />
        </div>
      </Section>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Card (raw shadcn primitive)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Reference only — <code>ui/card.tsx</code> is used directly by StatCard
          above and by several feature pages; no design-system wrapper needed.
        </CardContent>
      </Card>
    </div>
  );
}
